import React, { useContext } from 'react';

import Button from '../../Button/Button';
import { Icon } from '../../Icon';
import { WidgetContext } from '../../Widget/Widget';
import {
  badgeStyles,
  buttonOverrideStyles,
  triggerWrapperStyles,
} from './AskBuckButton.styles';

export type AskBuckButtonProps = {
  /**
   * Whether the border animation is active
   * @default true
   */
  isAnimating?: boolean;
  /**
   * Text to display in the button
   * @default "Ask Buck"
   */
  children?: React.ReactNode;
  /**
   * Number to display in the notification badge (0 or undefined = hidden)
   */
  badgeCount?: number;
};

/**
 * A specialized button component for the Buck AI assistant with an animated border.
 * This component is designed to be used as a trigger replacement in the ChatWidget.
 */
const AskBuckButton = React.forwardRef<HTMLElement, AskBuckButtonProps>(
  ({ isAnimating = true, children = 'Ask Buck', badgeCount }, ref) => {
    // Access Widget context if available
    const widgetContext = useContext(WidgetContext);
    const isExpanded = widgetContext?.expanded ?? false;
    const showBadge = !isExpanded && badgeCount !== undefined && badgeCount > 0;

    return (
      <div css={triggerWrapperStyles(isAnimating)}>
        <Button
          ref={ref}
          aria-expanded={widgetContext?.expanded}
          aria-haspopup="dialog"
          onClick={widgetContext?.toggle}
          icon={
            <>
              {showBadge && <span css={badgeStyles}>{badgeCount}</span>}
              <Icon
                variant={
                  isExpanded ? 'AngleDownSharp' : 'ConversationalSearchAi'
                }
                size="large"
              />
            </>
          }
          css={buttonOverrideStyles}
        >
          {isExpanded ? 'Close' : children}
        </Button>
      </div>
    );
  }
);

AskBuckButton.displayName = 'AskBuckButton';

export default AskBuckButton;
